import { useId, type CSSProperties, type ReactNode } from 'react';
import './stage-art.css';

export type StageArtKind =
  | 'story'
  | 'cast'
  | 'scene'
  | 'production'
  | 'finish'
  | 'campaign'
  | 'search'
  | 'editor'
  | 'talk'
  | 'vfx'
  | 'saas'
  | 'upscale'
  | 'crew'
  | 'review'
  | 'cost'
  | 'jobs'
  | 'settings'
  | 'slate';

function StoryArt() {
  return (
    <>
      <g className="art-page" transform="translate(10 0)">
        <rect x="128" y="30" width="78" height="98" rx="4" className="art-surface" />
        {[46, 58, 70, 82, 94, 106].map((y, index) => (
          <line
            key={y}
            className="art-draw"
            style={{ animationDelay: `${index * 180}ms` }}
            x1="140"
            x2={index === 2 ? 176 : index === 5 ? 168 : 194}
            y1={y}
            y2={y}
            pathLength={1}
          />
        ))}
        <rect className="art-caret" x="170" y="102" width="2" height="8" />
      </g>
      <g className="art-clapper" transform="translate(-6 0)">
        <rect x="30" y="66" width="98" height="62" rx="4" className="art-surface" />
        <line x1="30" x2="128" y1="88" y2="88" className="art-line" />
        <line x1="62" x2="62" y1="88" y2="128" className="art-line art-faint" />
        <text x="38" y="106" className="art-mono">SC 01</text>
        <text x="70" y="106" className="art-mono">TK 1</text>
        <text x="38" y="120" className="art-mono art-faint">INT. STUDIO</text>
        <rect x="30" y="72" width="98" height="12" rx="2" className="art-surface" />
        <path d="M40 72 L48 84 M58 72 L66 84 M76 72 L84 84 M94 72 L102 84 M112 72 L120 84" className="art-line" />
        <g className="art-clapper-arm">
          <rect x="30" y="56" width="98" height="12" rx="2" className="art-surface art-accent-stroke" />
          <path d="M40 56 L48 68 M58 56 L66 68 M76 56 L84 68 M94 56 L102 68 M112 56 L120 68" className="art-line art-accent" />
        </g>
      </g>
      <g className="art-strip">
        {Array.from({ length: 16 }, (_, index) => (
          <rect key={index} x={index * 18 - 18} y="136" width="10" height="4" rx="1" className="art-sprocket" />
        ))}
      </g>
    </>
  );
}

function CastArt() {
  const beam = `stage-art-beam-${useId().replace(/:/g, '')}`;
  const figures = [
    { x: 64, scale: 0.86 },
    { x: 120, scale: 1 },
    { x: 176, scale: 0.9 },
  ];
  return (
    <>
      <defs>
        <linearGradient id={beam} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="1" stopColor="currentColor" stopOpacity=".08" />
        </linearGradient>
      </defs>
      <ellipse cx="120" cy="124" rx="100" ry="12" className="art-floor" />
      <path className="art-sweep" fill={`url(#${beam})`} d="M120 14 L92 124 L148 124 Z" />
      {figures.map((figure, index) => (
        <g
          key={figure.x}
          className="art-figure"
          transform={`translate(${figure.x} 124) scale(${figure.scale})`}
          style={{ animationDelay: `${index * 1100}ms` }}
        >
          <circle cx="0" cy="-62" r="11" className="art-surface" />
          <path d="M-19 0 V-24 C-19 -38 -11 -46 0 -46 C11 -46 19 -38 19 -24 V0" className="art-surface" />
        </g>
      ))}
      <g className="art-lock-badge">
        <path d="M136 42 V38 a6 6 0 0 1 12 0 V42" className="art-accent" />
        <rect x="132" y="42" width="20" height="15" rx="3" className="art-accent-fill" />
        <line x1="142" x2="142" y1="47" y2="51" className="art-on-accent" />
      </g>
      <g className="art-pin">
        <path d="M212 40 c0 10 -12 22 -12 22 s-12 -12 -12 -22 a12 12 0 0 1 24 0" className="art-surface" />
        <circle cx="200" cy="40" r="4" className="art-line" />
      </g>
      <g className="art-cube">
        <path d="M30 48 l14 -7 l14 7 v16 l-14 7 l-14 -7 z M30 48 l14 7 l14 -7 M44 55 v16" className="art-line" />
      </g>
    </>
  );
}

function SceneArt() {
  const panels = [18, 92, 166];
  return (
    <>
      {panels.map((x, index) => (
        <g key={x} className="art-panel" style={{ animationDelay: `${index * 420}ms` }}>
          <rect x={x} y="20" width="58" height="40" rx="3" className="art-surface" />
          <line x1={x + 6} x2={x + 52} y1="46" y2="46" className="art-line art-faint art-draw" pathLength={1} style={{ animationDelay: `${index * 420 + 200}ms` }} />
          <circle cx={x + 18 + index * 10} cy="38" r="4" className="art-line" />
          <path d={`M${x + 18 + index * 10} 42 v8`} className="art-line" />
          <text x={x} y="72" className="art-mono art-faint">{`SH ${String(index + 1).padStart(2, '0')}`}</text>
        </g>
      ))}
      <path d="M26 118 C80 88 160 88 214 118" className="art-line art-dash" />
      <g className="art-camera-track">
        <g className="art-camera">
          <rect x="-12" y="-8" width="20" height="16" rx="2" className="art-surface art-accent-stroke" />
          <path d="M8 -4 L16 -8 V8 L8 4 Z" className="art-accent" />
          <path d="M16 0 L44 -14 M16 0 L44 14" className="art-line art-faint" />
        </g>
      </g>
      <circle cx="26" cy="118" r="3" className="art-dot" />
      <circle cx="214" cy="118" r="3" className="art-dot" />
    </>
  );
}

function ProductionArt() {
  const clip = `stage-art-strip-${useId().replace(/:/g, '')}`;
  return (
    <>
      <g className="art-camera-body">
        <rect x="46" y="62" width="92" height="50" rx="5" className="art-surface" />
        <path d="M138 76 L168 64 V110 L138 98 Z" className="art-surface" />
        <circle cx="62" cy="74" r="3" className="art-rec" />
        <text x="70" y="77" className="art-mono">REC</text>
        <text x="96" y="104" className="art-mono art-faint">00:00:04:12</text>
      </g>
      {[
        { cx: 66, cy: 38 },
        { cx: 116, cy: 38 },
      ].map((reel, index) => (
        <g key={reel.cx} className="art-reel" style={{ transformOrigin: `${reel.cx}px ${reel.cy}px`, animationDelay: `${index * -600}ms` }}>
          <circle cx={reel.cx} cy={reel.cy} r="22" className="art-surface" />
          <circle cx={reel.cx} cy={reel.cy} r="4" className="art-line" />
          {[0, 60, 120, 180, 240, 300].map((angle) => (
            <circle
              key={angle}
              cx={reel.cx + Math.cos((angle * Math.PI) / 180) * 13}
              cy={reel.cy + Math.sin((angle * Math.PI) / 180) * 13}
              r="4"
              className="art-line art-faint"
            />
          ))}
        </g>
      ))}
      <clipPath id={clip}>
        <rect x="168" y="68" width="72" height="38" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <g className="art-frames">
          {Array.from({ length: 8 }, (_, index) => (
            <g key={index} transform={`translate(${168 + index * 30} 68)`}>
              <rect x="0" y="0" width="28" height="38" className="art-surface" />
              <rect x="4" y="8" width="20" height="22" rx="1" className={index % 3 === 1 ? 'art-accent-soft' : 'art-frame-fill'} />
              <rect x="3" y="2" width="4" height="3" className="art-sprocket" />
              <rect x="21" y="2" width="4" height="3" className="art-sprocket" />
              <rect x="3" y="33" width="4" height="3" className="art-sprocket" />
              <rect x="21" y="33" width="4" height="3" className="art-sprocket" />
            </g>
          ))}
        </g>
      </g>
      <line x1="40" x2="176" y1="126" y2="126" className="art-line art-faint" />
      <path d="M92 112 L72 126 M92 112 L112 126 M92 112 V126" className="art-line art-faint" />
    </>
  );
}

function FinishArt() {
  const clips = [
    [{ x: 20, w: 46 }, { x: 70, w: 30 }, { x: 104, w: 52 }],
    [{ x: 20, w: 70 }, { x: 94, w: 62 }],
    [{ x: 20, w: 136 }],
  ];
  return (
    <>
      {clips.map((track, row) => (
        <g key={row}>
          {track.map((clip, index) => (
            <rect
              key={clip.x}
              x={clip.x}
              y={40 + row * 22}
              width={clip.w}
              height="14"
              rx="2"
              className={`art-clip ${row === 2 ? 'art-audio' : row === 0 && index === 1 ? 'art-accent-soft' : 'art-surface'}`}
              style={{ animationDelay: `${row * 160 + index * 90}ms` }}
            />
          ))}
        </g>
      ))}
      <g className="art-wave">
        {Array.from({ length: 22 }, (_, index) => (
          <rect key={index} x={24 + index * 6} y="86" width="2" height="8" rx="1" className="art-bar" style={{ animationDelay: `${index * 70}ms` }} />
        ))}
      </g>
      <g className="art-playhead">
        <path d="M0 28 L6 34 L0 40 L-6 34 Z" className="art-accent-fill" />
        <line x1="0" x2="0" y1="34" y2="112" className="art-accent" />
      </g>
      <g className="art-master">
        <circle cx="196" cy="70" r="30" className="art-surface" />
        <circle cx="196" cy="70" r="30" className="art-ring art-accent" pathLength={1} />
        <path d="M184 70 l8 8 l16 -16" className="art-check" pathLength={1} />
        <text x="178" y="122" className="art-mono art-faint">MASTER</text>
      </g>
    </>
  );
}

function CampaignArt() {
  return (
    <>
      <line x1="18" x2="222" y1="130" y2="130" className="art-line art-faint" />
      <g className="art-post">
        <rect x="22" y="44" width="54" height="54" rx="4" className="art-surface" />
        <rect x="28" y="50" width="42" height="30" rx="2" className="art-frame-fill" />
        <circle cx="40" cy="62" r="5" className="art-line art-faint" />
        <path d="M30 78 L44 68 L54 74 L68 64" className="art-line art-faint" />
        <g className="art-heart">
          <path d="M31 89 c0-3 4-4 5-1 c1-3 5-2 5 1 c0 3-5 6-5 6 s-5-3-5-6z" className="art-accent-fill" />
        </g>
        <text x="46" y="93" className="art-mono art-count">+128</text>
      </g>
      <g className="art-phone">
        <rect x="92" y="18" width="58" height="104" rx="8" className="art-surface" />
        {[0, 1, 2].map((index) => (
          <g key={index}>
            <rect x={99 + index * 15} y="25" width="13" height="2" rx="1" className="art-frame-fill" />
            <rect x={99 + index * 15} y="25" width="13" height="2" rx="1" className="art-story art-accent-fill" style={{ animationDelay: `${index * 1500}ms` }} />
          </g>
        ))}
        <rect x="104" y="40" width="34" height="42" rx="3" className="art-frame-fill" />
        <g className="art-product">
          <rect x="114" y="52" width="14" height="24" rx="3" className="art-surface art-accent-stroke" />
          <rect x="117" y="46" width="8" height="6" rx="1" className="art-surface" />
        </g>
        {[92, 100].map((y, index) => (
          <line key={y} className="art-draw" style={{ animationDelay: `${600 + index * 220}ms` }} x1="102" x2={index ? 126 : 140} y1={y} y2={y} pathLength={1} />
        ))}
        <rect x="102" y="106" width="38" height="9" rx="2" className="art-accent-soft" />
        <text x="110" y="113" className="art-mono">SHOP</text>
      </g>
      <g className="art-banner">
        <rect x="164" y="46" width="60" height="34" rx="3" className="art-surface" />
        <line x1="170" x2="200" y1="56" y2="56" className="art-line" />
        <line x1="170" x2="190" y1="63" y2="63" className="art-line art-faint" />
        <rect x="170" y="69" width="20" height="6" rx="1.5" className="art-accent-fill" />
        <text x="202" y="76" className="art-mono art-faint">16:9</text>
      </g>
      <g className="art-cursor-track">
        <path d="M0 0 L0 11 L3 8 L6 13 L8 12 L5 7 L9 7 Z" className="art-surface" />
      </g>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <circle key={index} cx={78 + index * 17} cy="138" r="2.5" className="art-ladder" style={{ animationDelay: `${index * 380}ms` }} />
      ))}
    </>
  );
}

function SearchArt() {
  return (
    <>
      <rect x="40" y="28" width="120" height="80" rx="6" className="art-surface" />
      {[46, 60, 74, 88].map((y, index) => (
        <line key={y} x1="54" x2={index % 2 ? 120 : 144} y1={y} y2={y} className="art-line art-faint" />
      ))}
      <g className="art-lens">
        <circle cx="150" cy="84" r="20" className="art-surface art-accent-stroke" />
        <path d="M164 98 L184 118" className="art-accent" />
      </g>
    </>
  );
}

function EditorArt() {
  const v1 = [
    { x: 20, w: 62 },
    { x: 86, w: 44 },
    { x: 134, w: 86 },
  ];
  return (
    <>
      <rect x="70" y="8" width="100" height="54" rx="3" className="art-surface" />
      <rect x="76" y="14" width="88" height="42" rx="1" className="art-frame-fill" />
      <path d="M114 28 L128 35 L114 42 Z" className="art-line art-faint" />
      <text x="96" y="72" className="art-mono art-faint">01:02:14:08</text>
      <g className="art-cut-left">
        {v1.slice(0, 2).map((clip, index) => (
          <rect key={clip.x} x={clip.x} y="84" width={clip.w} height="14" rx="2" className={index === 1 ? 'art-accent-soft' : 'art-surface'} />
        ))}
      </g>
      <g className="art-cut-right">
        <rect x={v1[2].x} y="84" width={v1[2].w} height="14" rx="2" className="art-surface" />
      </g>
      <rect x="40" y="102" width="120" height="10" rx="2" className="art-surface art-faint" />
      <g>
        {Array.from({ length: 30 }, (_, index) => (
          <rect key={index} x={22 + index * 6.6} y="120" width="2" height="8" rx="1" className="art-bar" style={{ animationDelay: `${index * 60}ms` }} />
        ))}
      </g>
      <g className="art-blade">
        <path d="M132 70 h8 l-4 8 z" className="art-accent-fill" />
        <line x1="136" x2="136" y1="80" y2="100" className="art-accent art-blade-cut" />
      </g>
      <g className="art-scrub">
        <path d="M0 76 L5 81 L0 86 L-5 81 Z" className="art-accent-fill" />
        <line x1="0" x2="0" y1="81" y2="132" className="art-accent" />
      </g>
    </>
  );
}

function TalkArt() {
  return (
    <>
      <rect x="22" y="14" width="86" height="114" rx="4" className="art-surface" />
      <rect x="28" y="20" width="74" height="102" rx="2" className="art-frame-fill" />
      <circle cx="65" cy="58" r="20" className="art-surface" />
      <path d="M33 122 C33 96 45 84 65 84 C85 84 97 96 97 122" className="art-surface" />
      <g className="art-blink">
        <line x1="57" x2="61" y1="54" y2="54" className="art-line" />
        <line x1="69" x2="73" y1="54" y2="54" className="art-line" />
      </g>
      <ellipse cx="65" cy="67" rx="5" ry="2.5" className="art-mouth art-accent-fill" />
      <circle cx="36" cy="28" r="2.5" className="art-rec" />
      <text x="42" y="31" className="art-mono">LIVE</text>
      <g className="art-bubble">
        <path d="M124 22 h92 a4 4 0 0 1 4 4 v34 a4 4 0 0 1 -4 4 h-78 l-8 8 v-8 h-6 a4 4 0 0 1 -4 -4 v-34 a4 4 0 0 1 4 -4 z" className="art-surface" />
        {[34, 44, 54].map((y, index) => (
          <line key={y} className="art-draw" style={{ animationDelay: `${index * 260}ms` }} x1="132" x2={index === 2 ? 180 : 210} y1={y} y2={y} pathLength={1} />
        ))}
      </g>
      <g>
        {Array.from({ length: 16 }, (_, index) => (
          <rect key={index} x={128 + index * 6} y="96" width="3" height="14" rx="1.5" className={`art-bar${index % 4 === 1 ? ' art-bar-accent' : ''}`} style={{ animationDelay: `${index * 55}ms` }} />
        ))}
      </g>
      <text x="128" y="128" className="art-mono art-faint">VO · TAKE 03</text>
    </>
  );
}

function VfxArt() {
  const markers = [
    { x: 46, y: 36 },
    { x: 142, y: 44 },
    { x: 58, y: 100 },
  ];
  return (
    <>
      <rect x="18" y="14" width="150" height="104" rx="3" className="art-surface" />
      <rect x="24" y="20" width="138" height="92" rx="1" className="art-frame-fill" />
      <path d="M24 88 L58 62 L78 76 L110 48 L162 86" className="art-line art-faint" />
      <path
        d="M92 104 C88 86 90 70 98 62 C104 56 114 56 118 64 C124 74 124 90 120 104 Z"
        className="art-roto"
        pathLength={1}
      />
      {[
        [92, 104],
        [98, 62],
        [118, 64],
        [120, 104],
      ].map(([x, y], index) => (
        <rect key={`${x}-${y}`} x={x - 2} y={y - 2} width="4" height="4" className="art-roto-point" style={{ animationDelay: `${600 + index * 260}ms` }} />
      ))}
      {markers.map((marker, index) => (
        <g key={marker.x} className="art-marker" transform={`translate(${marker.x} ${marker.y})`}>
          <g style={{ animationDelay: `${index * 500}ms` }}>
            <path d="M-6 0 H-2 M2 0 H6 M0 -6 V-2 M0 2 V6" />
            <rect x="-8" y="-8" width="16" height="16" rx="1" className="art-marker-box" />
          </g>
        </g>
      ))}
      {['COMP', 'MATTE', 'PLATE'].map((label, index) => (
        <g key={label} className="art-layer" style={{ animationDelay: `${index * -400}ms` }}>
          <rect x="180" y={30 + index * 28} width="44" height="22" rx="2" className={index === 1 ? 'art-accent-soft' : 'art-surface'} />
          <text x="186" y={44 + index * 28} className="art-mono art-faint">{label}</text>
        </g>
      ))}
    </>
  );
}

function SaasArt() {
  return (
    <>
      <g className="art-sheet art-sheet-base">
        <rect x="30" y="26" width="140" height="96" rx="5" className="art-surface" />
        <line x1="30" x2="170" y1="40" y2="40" className="art-line art-faint" />
        {[40, 48, 56].map((x) => (
          <circle key={x} cx={x} cy="33" r="2" className="art-dot" />
        ))}
        <rect x="38" y="48" width="30" height="66" rx="2" className="art-frame-fill" />
      </g>
      <g className="art-sheet art-sheet-card">
        <rect x="78" y="50" width="82" height="42" rx="3" className="art-surface" />
        <line x1="86" x2="130" y1="62" y2="62" className="art-line" />
        <line x1="86" x2="118" y1="70" y2="70" className="art-line art-faint" />
      </g>
      <g className="art-sheet art-sheet-button">
        <rect x="120" y="96" width="40" height="14" rx="3" className="art-accent-fill" />
      </g>
      <path d="M178 58 L208 44 M178 90 L208 90 M178 112 L208 128" className="art-line art-dash art-faint" />
      <text x="190" y="42" className="art-mono art-faint">L3</text>
      <text x="212" y="93" className="art-mono art-faint">L2</text>
      <text x="212" y="132" className="art-mono art-faint">L1</text>
      <g className="art-saas-cursor">
        <path d="M0 0 L0 11 L3 8 L6 13 L8 12 L5 7 L9 7 Z" className="art-surface" />
      </g>
    </>
  );
}

function UpscaleArt() {
  const clip = `stage-art-upscale-${useId().replace(/:/g, '')}`;
  const cells = Array.from({ length: 48 }, (_, index) => index);
  return (
    <>
      <rect x="30" y="14" width="180" height="104" rx="3" className="art-surface" />
      <g>
        {cells.map((index) => {
          const col = index % 8;
          const row = Math.floor(index / 8);
          const tone = (col * 7 + row * 11) % 5;
          return (
            <rect
              key={index}
              x={36 + col * 21}
              y={20 + row * 16}
              width="20"
              height="15"
              className={tone === 0 ? 'art-accent-soft' : 'art-frame-fill'}
              style={{ opacity: 0.35 + tone * 0.15 }}
            />
          );
        })}
      </g>
      <clipPath id={clip}>
        <rect x="36" y="20" width="168" height="92" className="art-wipe" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <rect x="36" y="20" width="168" height="92" className="art-sharp" />
        <circle cx="160" cy="48" r="12" className="art-accent" />
        <path d="M36 100 L78 64 L104 84 L136 56 L204 104" className="art-line" />
        <path d="M36 112 L204 112" className="art-line art-faint" />
      </g>
      <g className="art-wipe-line">
        <line x1="0" x2="0" y1="16" y2="116" className="art-accent" />
        <circle cx="0" cy="66" r="5" className="art-accent-fill" />
      </g>
      <text x="30" y="134" className="art-mono art-faint">1080p</text>
      <path d="M62 131 h18 m-4 -3 l4 3 l-4 3" className="art-line art-faint" />
      <text x="86" y="134" className="art-mono">2160p</text>
    </>
  );
}

function CrewArt() {
  const nodes = [
    { x: 44, y: 34, label: 'DR' },
    { x: 196, y: 34, label: 'DP' },
    { x: 44, y: 112, label: 'ED' },
    { x: 196, y: 112, label: 'VO' },
  ];
  return (
    <>
      {nodes.map((node) => (
        <line key={node.label} x1="120" y1="72" x2={node.x} y2={node.y} className="art-line art-faint art-dash" />
      ))}
      {nodes.map((node, index) => (
        <circle
          key={`pulse-${node.label}`}
          cx="120"
          cy="72"
          r="2.5"
          className="art-pulse"
          style={{ '--dx': `${node.x - 120}px`, '--dy': `${node.y - 72}px`, animationDelay: `${index * 700}ms` } as CSSProperties}
        />
      ))}
      {nodes.map((node, index) => (
        <g key={node.label} className="art-node" style={{ animationDelay: `${index * 700 + 900}ms` }}>
          <circle cx={node.x} cy={node.y} r="15" className="art-surface" />
          <text x={node.x - 5} y={node.y + 2.5} className="art-mono">{node.label}</text>
        </g>
      ))}
      <circle cx="120" cy="72" r="30" className="art-halo" />
      <circle cx="120" cy="72" r="22" className="art-surface art-accent-stroke" />
      <path d="M120 60 L131 80 H109 Z" className="art-accent" />
      <line x1="115" x2="125" y1="75" y2="75" className="art-accent" />
    </>
  );
}

function ReviewArt() {
  return (
    <>
      <rect x="18" y="16" width="144" height="84" rx="3" className="art-surface" />
      <rect x="24" y="22" width="132" height="66" rx="1" className="art-frame-fill" />
      <path d="M84 44 L100 55 L84 66 Z" className="art-line art-faint" />
      <line x1="24" x2="156" y1="94" y2="94" className="art-line art-faint" />
      <line x1="24" x2="156" y1="94" y2="94" className="art-review-progress art-accent" pathLength={1} />
      {[
        { x: 52, n: 1 },
        { x: 98, n: 2 },
        { x: 136, n: 3 },
      ].map((pin, index) => (
        <g key={pin.n} className="art-comment" style={{ animationDelay: `${600 + index * 900}ms` }}>
          <path d={`M${pin.x} 94 l-6 -8 a8 8 0 1 1 12 0 z`} className="art-accent-fill" />
          <text x={pin.x - 2} y="84" className="art-mono art-on-accent-text">{pin.n}</text>
        </g>
      ))}
      <rect x="24" y="108" width="56" height="20" rx="3" className="art-surface" />
      <text x="32" y="121" className="art-mono art-faint">NOTES</text>
      <line x1="88" x2="150" y1="114" y2="114" className="art-draw" pathLength={1} style={{ animationDelay: '900ms' }} />
      <line x1="88" x2="132" y1="122" y2="122" className="art-draw" pathLength={1} style={{ animationDelay: '1200ms' }} />
      <g className="art-master">
        <circle cx="196" cy="70" r="30" className="art-surface" />
        <circle cx="196" cy="70" r="30" className="art-ring art-accent" pathLength={1} />
        <path d="M184 70 l8 8 l16 -16" className="art-check" pathLength={1} />
        <text x="176" y="122" className="art-mono art-faint">APPROVE</text>
      </g>
    </>
  );
}

function CostArt() {
  const bars = [30, 48, 40, 62, 52, 66];
  return (
    <>
      <path d="M26 104 A44 44 0 0 1 114 104" className="art-line art-faint" />
      <path d="M26 104 A44 44 0 0 1 114 104" className="art-gauge art-accent" pathLength={1} />
      <g className="art-needle">
        <line x1="70" x2="70" y1="104" y2="70" className="art-line" />
      </g>
      <circle cx="70" cy="104" r="4" className="art-accent-fill" />
      <text x="50" y="126" className="art-mono">$ 42.80</text>
      <line x1="130" x2="226" y1="44" y2="44" className="art-line art-dash art-warning-line" />
      <text x="190" y="38" className="art-mono art-faint">BUDGET</text>
      {bars.map((height, index) => (
        <rect
          key={index}
          x={134 + index * 15}
          y={116 - height}
          width="10"
          height={height}
          rx="1.5"
          className={`art-rise ${index === bars.length - 1 ? 'art-accent-soft' : 'art-surface'}`}
          style={{ animationDelay: `${index * 140}ms` }}
        />
      ))}
      <line x1="128" x2="228" y1="116" y2="116" className="art-line art-faint" />
    </>
  );
}

function JobsArt() {
  const rows = [
    { label: 'SH 14 · VIDEO', state: 'run' },
    { label: 'SH 15 · STILL', state: 'run' },
    { label: 'SH 16 · STILL', state: 'queue' },
    { label: 'SH 13 · VIDEO', state: 'done' },
  ];
  return (
    <>
      {rows.map((row, index) => {
        const y = 14 + index * 30;
        return (
          <g key={row.label} className="art-job" style={{ animationDelay: `${index * 160}ms` }}>
            <rect x="26" y={y} width="188" height="24" rx="3" className={row.state === 'queue' ? 'art-surface art-dash' : 'art-surface'} />
            {row.state === 'done' ? (
              <path d={`M36 ${y + 12} l3 3 l6 -6`} className="art-accent" />
            ) : (
              <circle cx="40" cy={y + 12} r="3" className={row.state === 'run' ? 'art-rec' : 'art-dot'} />
            )}
            <text x="52" y={y + 15} className="art-mono">{row.label}</text>
            <rect x="134" y={y + 10} width="70" height="4" rx="2" className="art-frame-fill" />
            {row.state !== 'queue' ? (
              <rect
                x="134"
                y={y + 10}
                width="70"
                height="4"
                rx="2"
                className={row.state === 'done' ? 'art-accent-fill' : 'art-job-fill art-accent-fill'}
                style={{ animationDelay: `${index * 900}ms` }}
              />
            ) : null}
          </g>
        );
      })}
    </>
  );
}

function SettingsArt() {
  return (
    <>
      {[
        { y: 30, delay: 0 },
        { y: 56, delay: -1400 },
        { y: 82, delay: -2600 },
      ].map((slider) => (
        <g key={slider.y}>
          <line x1="24" x2="140" y1={slider.y} y2={slider.y} className="art-line art-faint" />
          <line x1="24" x2="140" y1={slider.y} y2={slider.y} className="art-accent art-knob-fill" style={{ animationDelay: `${slider.delay}ms` }} />
          <circle cx="24" cy={slider.y} r="6" className="art-surface art-accent-stroke art-knob" style={{ animationDelay: `${slider.delay}ms` }} />
        </g>
      ))}
      {[108, 126].map((y, index) => (
        <g key={y}>
          <rect x="24" y={y - 6} width="24" height="12" rx="6" className="art-surface" />
          <circle cx="30" cy={y} r="4" className={`art-toggle${index ? ' art-toggle-late' : ''}`} />
          <line x1="58" x2={index ? 110 : 128} y1={y} y2={y} className="art-line art-faint" />
        </g>
      ))}
      <g className="art-gear">
        <circle cx="186" cy="72" r="18" className="art-surface" />
        <circle cx="186" cy="72" r="6" className="art-line" />
        {Array.from({ length: 8 }, (_, index) => {
          const angle = (index * Math.PI) / 4;
          return (
            <line
              key={index}
              x1={186 + Math.cos(angle) * 20}
              y1={72 + Math.sin(angle) * 20}
              x2={186 + Math.cos(angle) * 27}
              y2={72 + Math.sin(angle) * 27}
              className="art-line"
            />
          );
        })}
      </g>
    </>
  );
}

function SlateArt() {
  const cards = [0, 1, 2, 3, 4, 5];
  return (
    <>
      {cards.map((index) => {
        const x = 20 + (index % 3) * 70;
        const y = 12 + Math.floor(index / 3) * 64;
        const active = index === 4;
        return (
          <g key={index} className="art-panel" style={{ animationDelay: `${index * 160}ms` }}>
            <rect x={x} y={y} width="62" height="56" rx="3" className={`art-surface${active ? ' art-accent-stroke' : ''}`} />
            <rect x={x + 5} y={y + 5} width="52" height="30" rx="1" className={active ? 'art-accent-soft' : 'art-frame-fill'} />
            <line x1={x + 6} x2={x + 44} y1={y + 43} y2={y + 43} className="art-line" />
            <line x1={x + 6} x2={x + 30} y1={y + 50} y2={y + 50} className="art-line art-faint" />
            {active ? <rect x={x + 5} y={y + 5} width="3" height="30" className="art-scan art-accent-fill" /> : null}
          </g>
        );
      })}
    </>
  );
}

const ART: Record<StageArtKind, () => ReactNode> = {
  story: StoryArt,
  cast: CastArt,
  scene: SceneArt,
  production: ProductionArt,
  finish: FinishArt,
  campaign: CampaignArt,
  search: SearchArt,
  editor: EditorArt,
  talk: TalkArt,
  vfx: VfxArt,
  saas: SaasArt,
  upscale: UpscaleArt,
  crew: CrewArt,
  review: ReviewArt,
  cost: CostArt,
  jobs: JobsArt,
  settings: SettingsArt,
  slate: SlateArt,
};

export default function StageArt({ kind, className = '' }: { kind: StageArtKind; className?: string }) {
  const Art = ART[kind];
  return (
    <svg
      className={`stage-art stage-art--${kind}${className ? ` ${className}` : ''}`}
      viewBox="0 0 240 144"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <Art />
    </svg>
  );
}

export function StageEmpty({
  art,
  title,
  description,
  action,
  compact = false,
}: {
  art: StageArtKind;
  title: string;
  description: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`studio-stage-empty studio-stage-empty--art${compact ? ' is-compact' : ''}`}>
      <StageArt kind={art} />
      <h3>{title}</h3>
      <p>{description}</p>
      {action ? <div className="studio-stage-empty-actions">{action}</div> : null}
    </div>
  );
}
